import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { Link } from "../atoms/Link";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export interface TaskLauncherItem {
	id: string;
	title: string;
	description: string;
	icon?: ReactNode;
	href?: string;
	onClick?: () => void;
}

export interface TaskLauncherProps {
	items: TaskLauncherItem[];
	columns?: 2 | 3;
	/**
	 * Heading level for each card title. Defaults to "h3". Pass "h2" when the
	 * launcher sits directly under a page `<h1>` to avoid an h1→h3 skip
	 * (WCAG 1.3.1).
	 */
	headingAs?: "h2" | "h3";
	className?: string;
}

const COLUMN_CLASSES: Record<2 | 3, string> = {
	2: "sm:grid-cols-2",
	3: "sm:grid-cols-2 md:grid-cols-3",
};

/** Shared visual shell, free of any interactive affordances. */
const CARD_BASE_CLASSES =
	"flex h-full flex-col gap-2 rounded-lg border border-border bg-card p-4 text-left text-foreground no-underline transition-colors";

/** Adds hover/focus affordances for cards that are actually clickable. */
const CARD_INTERACTIVE_CLASSES = `${CARD_BASE_CLASSES} ring-offset-background hover:border-primary hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`;

function TaskCardContents({
	item,
	headingAs,
}: {
	item: TaskLauncherItem;
	headingAs: "h2" | "h3";
}): ReactNode {
	return (
		<>
			{item.icon && (
				<span className="text-primary" aria-hidden="true">
					{item.icon}
				</span>
			)}
			<Heading as={headingAs} size="md">
				{item.title}
			</Heading>
			<Text size="sm" textColor="mutedForeground">
				{item.description}
			</Text>
		</>
	);
}

function TaskLauncher({
	items,
	columns = 3,
	headingAs = "h3",
	className,
}: TaskLauncherProps): ReactNode {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-4",
				COLUMN_CLASSES[columns],
				className,
			)}
		>
			{items.map((item) => {
				const accessibleName = `${item.title}. ${item.description}`;

				if (item.href) {
					return (
						<Link
							key={item.id}
							href={item.href}
							aria-label={accessibleName}
							className={CARD_INTERACTIVE_CLASSES}
						>
							<TaskCardContents item={item} headingAs={headingAs} />
						</Link>
					);
				}

				if (item.onClick) {
					return (
						<button
							key={item.id}
							type="button"
							onClick={item.onClick}
							aria-label={accessibleName}
							className={CARD_INTERACTIVE_CLASSES}
						>
							<TaskCardContents item={item} headingAs={headingAs} />
						</button>
					);
				}

				return (
					<div key={item.id} className={CARD_BASE_CLASSES}>
						<TaskCardContents item={item} headingAs={headingAs} />
					</div>
				);
			})}
		</div>
	);
}
TaskLauncher.displayName = "TaskLauncher";

export default TaskLauncher;
export { TaskLauncher };
